import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TrendingUp, MapPin, Search, ArrowRight, Users, Info } from 'lucide-react';
import { colors, spacing, typography, borderRadius, shadows, breakpoints } from '../styles/theme';
import { useTranslations } from '../locales/useI18n';
import { useAnalytics } from '../hooks/useAnalytics';
import TopCities from '../components/TopCities';
import EventCard from '../components/EventCard';
import EventFinderAPI from '../services/api';
import { Event } from '../types/api';
import config from '../config';
import SEO from '../components/SEO';
import HomeActivityStats from '../components/home/HomeActivityStats';

const HomePage = styled.div`
  width: 100%;
  position: relative;
`;

const BetaRibbon = styled.a`
  position: fixed;
  top: 0;
  right: 0;
  width: 200px;
  height: 60px;
  background: linear-gradient(45deg, #ff6b35, #ff8c42);
  color: ${colors.white};
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  text-align: center;
  transform: rotate(45deg) translate(50px, -10px);
  transform-origin: center;
  z-index: 1001;
  box-shadow: ${shadows.lg};
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(45deg, #ff5722, #ff7043);
    transform: rotate(45deg) translate(50px, -10px) scale(1.05);
    text-decoration: none;
    color: ${colors.white};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    transform: skew(-10deg);
  }
  
  @media (max-width: ${breakpoints.md}) {
    width: 180px;
    height: 50px;
    font-size: ${typography.fontSizes.xs};
    transform: rotate(45deg) translate(45px, -8px);
    
    &:hover {
      transform: rotate(45deg) translate(45px, -8px) scale(1.05);
    }
  }
  
  @media (max-width: ${breakpoints.sm}) {
    width: 160px;
    height: 45px;
    font-size: ${typography.fontSizes.xs};
    transform: rotate(45deg) translate(40px, -6px);
    
    &:hover {
      transform: rotate(45deg) translate(40px, -6px) scale(1.05);
    }
  }
`;

const BetaText = styled.span`
  position: relative;
  z-index: 2;
  line-height: 1.2;
`;

const HeroSection = styled.section`
  background: ${colors.gradients.hero};
  color: ${colors.white};
  padding: ${spacing[16]} 0 ${spacing[12]};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
  
  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[12]} 0 ${spacing[8]};
  }

  @media (max-width: ${breakpoints.sm}) {
    padding: ${spacing[6]} 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${spacing[4]};
  position: relative;
  z-index: 1;
  
  @media (min-width: ${breakpoints.sm}) {
    padding: 0 ${spacing[6]};
  }
  
  @media (min-width: ${breakpoints.lg}) {
    padding: 0 ${spacing[8]};
  }
`;

const HeroTitle = styled.h1`
  font-size: ${typography.fontSizes['5xl']};
  font-weight: ${typography.fontWeights.bold};
  margin-bottom: ${spacing[6]};
  line-height: ${typography.lineHeights.tight};
  color: ${colors.white};
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes['4xl']};
  }
  
  @media (max-width: ${breakpoints.sm}) {
    font-size: ${typography.fontSizes['3xl']};
    margin-bottom: ${spacing[3]};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${typography.fontSizes.xl};
  margin-bottom: ${spacing[6]};
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  color: ${colors.white};
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes.lg};
  }

  @media (max-width: ${breakpoints.sm}) {
    margin-bottom: ${spacing[4]};
  }
`;

const QuickActions = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing[4]};
  margin-top: 0;
  
  @media (max-width: ${breakpoints.sm}) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[2]};
    width: 100%;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[4]} ${spacing[6]};
  background: ${colors.white};
  color: ${colors.primary[600]};
  border: none;
  border-radius: ${borderRadius.xl};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${shadows.lg};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.xl};
    background: ${colors.primary[50]};
  }
  
  &.secondary {
    background: transparent;
    color: ${colors.white};
    border: 2px solid ${colors.white};
    
    &:hover {
      background: ${colors.white};
      color: ${colors.primary[600]};
    }
  }
  
  @media (max-width: ${breakpoints.sm}) {
    width: 100%;
    min-height: 48px;
    padding: ${spacing[3]};
    gap: ${spacing[1]};
    font-size: ${typography.fontSizes.sm};
    justify-content: center;
  }
`;

const Section = styled.section`
  padding: ${spacing[12]} 0;
  
  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[8]} 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${spacing[4]};
  
  @media (min-width: ${breakpoints.sm}) {
    padding: 0 ${spacing[6]};
  }
  
  @media (min-width: ${breakpoints.lg}) {
    padding: 0 ${spacing[8]};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${spacing[8]};
`;

const SectionTitle = styled.h2`
  font-size: ${typography.fontSizes['3xl']};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[4]};
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes['2xl']};
  }
`;

const SectionDescription = styled.p`
  font-size: ${typography.fontSizes.lg};
  color: ${colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes.base};
  }
`;

const PopularEventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${spacing[6]};
  margin-bottom: ${spacing[8]};
  
  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[4]};
  }
`;

const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin: 0 auto;
  padding: ${spacing[3]} ${spacing[6]};
  background: ${colors.primary[500]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.medium};
  cursor: pointer;
  transition: background-color 0.15s ease;
  
  &:hover {
    background: ${colors.primary[600]};
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${spacing[8]};
  margin-top: ${spacing[8]};
  
  @media (max-width: ${breakpoints.sm}) {
    gap: ${spacing[6]};
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: ${spacing[6]};
  background: ${colors.white};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.sm};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${colors.primary[100]};
  border-radius: ${borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing[4]} auto;
  color: ${colors.primary[600]};
`;

const FeatureTitle = styled.h3`
  font-size: ${typography.fontSizes.xl};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[3]};
`;

const FeatureDescription = styled.p`
  font-size: ${typography.fontSizes.base};
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeights.relaxed};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${spacing[12]};
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${colors.border.light};
  border-top: 3px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

export const HomePageComponent: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { trackEvent, trackEventView } = useAnalytics();
  const [popularEvents, setPopularEvents] = useState<Event[]>([]);
  const [isLoadingPopular, setIsLoadingPopular] = useState(true);

  useEffect(() => {
    const fetchPopularEvents = async () => {
      try {
        const response = await EventFinderAPI.getPopularEvents(18);
        // Dedupe by eventId in case an older endpoint returns one row per
        // linked entity.
        const seen = new Set<number>();
        const unique: Event[] = [];
        for (const ev of response.events) {
          if (ev?.eventId == null || seen.has(ev.eventId)) continue;
          seen.add(ev.eventId);
          unique.push(ev);
          if (unique.length >= 18) break;
        }
        setPopularEvents(unique);
      } catch (error) {
        console.error('Error fetching popular events:', error);
      } finally {
        setIsLoadingPopular(false);
      }
    };

    fetchPopularEvents();
  }, []);

  const handleEventClick = (event: Event) => {
    trackEvent({ category: 'home', action: 'event_select', custom_parameters: { action_location: 'popular_events', event_type: event.isVirtual ? 'virtual' : 'in_person' } });
    trackEventView(event.eventId.toString(), event.title, {
      source: 'homepage_popular',
      event_type: event.isVirtual ? 'virtual' : 'in_person',
    });
    navigate(`/event/${event.eventId}`);
  };

  const handleQuickNavigation = (path: string, action: string) => {
    trackEvent({ category: 'navigation', action, custom_parameters: { action_location: 'home_quick_actions', destination: path } });
    navigate(path);
  };

  return (
    <HomePage>
      <SEO
        title="Event Finder | Discover Events Near You and Online"
        description="Discover amazing events worldwide with our AI-powered search platform. Find conferences, workshops, training sessions, and networking events near you. Smart search, interactive maps, and personalized recommendations."
        keywords="events, event finder, conferences, workshops, training, AI-powered search, event discovery, networking events, tech events, business events"
        canonical="https://www.eventfinder.site/"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Event Finder",
            "applicationCategory": "EventApplication",
            "description": "AI-powered event discovery platform helping users find conferences, workshops, training sessions, and networking events worldwide.",
            "url": "https://www.eventfinder.site",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          },
          ...(popularEvents.length > 0 ? [{
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Popular Events",
            "description": "Events connected to the most organizations on Event Finder",
            "numberOfItems": popularEvents.length,
            "itemListElement": popularEvents.map((event, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": event.title,
              "url": `https://www.eventfinder.site/event/${event.eventId}`
            }))
          }] : [])
        ]}
      />
      
      {/* Beta Ribbon */}
      <BetaRibbon 
        href={config.app.feedbackFormUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        title="Site is in beta - Click to leave feedback"
      >
        <BetaText>
          BETA<br />
          {t('header.feedback')}?
        </BetaText>
      </BetaRibbon>
      
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>{t('homepage.heroTitle')}</HeroTitle>
          <HeroSubtitle>
            {t('homepage.heroSubtitle')}
          </HeroSubtitle>
          <HomeActivityStats />
          <QuickActions>
            <ActionButton onClick={() => handleQuickNavigation('/browse', 'browse_click')}>
              <Search size={20} />
              {t('homepage.exploreEvents')}
            </ActionButton>
            <ActionButton className="secondary" onClick={() => handleQuickNavigation('/how-it-works', 'how_it_works_click')}>
              <Info size={20} />
              {t('header.howItWorks')}
            </ActionButton>
            <ActionButton className="secondary" onClick={() => handleQuickNavigation('/map', 'map_click')}>
              <MapPin size={20} />
              {t('header.mapView')}
            </ActionButton>
            <ActionButton className="secondary" onClick={() => handleQuickNavigation('/groups/new', 'create_group_click')}>
              <Users size={20} />
              {t('header.createGroup')}
            </ActionButton>
          </QuickActions>
        </HeroContent>
      </HeroSection>

      {/* Top Cities Section */}
      <Section>
        <Container>
          <TopCities 
            showTitle={true}
          />
        </Container>
      </Section>

      {/* Popular Events Section */}
      <Section style={{ background: colors.background.secondary }}>
        <Container>
          <SectionHeader>
            <SectionTitle>{t('homepage.popularEvents')}</SectionTitle>
            <SectionDescription>
              Upcoming events connected to the most organizations over the next three calendar months
            </SectionDescription>
          </SectionHeader>

          {isLoadingPopular ? (
            <LoadingContainer>
              <LoadingSpinner />
            </LoadingContainer>
          ) : (
            <>
              <PopularEventsGrid>
                {popularEvents.map((event) => (
                  <EventCard
                    key={event.eventId}
                    event={event}
                    onClick={() => handleEventClick(event)}
                  />
                ))}
              </PopularEventsGrid>
              
              {popularEvents.length > 0 && (
                <div style={{ textAlign: 'center' }}>
                  <ViewAllButton onClick={() => handleQuickNavigation('/browse', 'popular_view_all')}>
                    {t('homepage.viewAllEvents')}
                    <ArrowRight size={16} />
                  </ViewAllButton>
                </div>
              )}
            </>
          )}
        </Container>
      </Section>

      {/* Features Section */}
      <Section>
        <Container>
          <SectionHeader>
            <SectionTitle>Why Choose Event Finder?</SectionTitle>
            <SectionDescription>
              Everything you need to discover and attend amazing events
            </SectionDescription>
          </SectionHeader>

          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon>
                <Search size={32} />
              </FeatureIcon>
              <FeatureTitle>Smart Search</FeatureTitle>
              <FeatureDescription>
                Use our AI-powered search to find events that match your interests and preferences with intelligent suggestions
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <MapPin size={32} />
              </FeatureIcon>
              <FeatureTitle>Location-Based Discovery</FeatureTitle>
              <FeatureDescription>
                Find events near you or explore opportunities in different cities with our interactive map and location filters
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>
                <TrendingUp size={32} />
              </FeatureIcon>
              <FeatureTitle>Popular Event Signals</FeatureTitle>
              <FeatureDescription>
                See which upcoming events connect the most participating organizations across the next three calendar months
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </Container>
      </Section>
    </HomePage>
  );
};

export default HomePageComponent;
